import { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, ResolveFn, RouterStateSnapshot } from '@angular/router';
import { ENVIRONMENT } from '../../../../environments/environment.interface';
import { ApiResponse, Metadata } from '../../../api/api-types/api-response.api';
import {
	Assignment,
	AssignmentEndOfCare,
	AssignmentWithRelations,
	AssingmentImpedimentReportMinMaxDueDate,
} from '../../../api/app-types/assignment';
import { DB_PHASES } from '../../../api/app-types/phase.app';
import { ValidationPhaseResponse } from '../../../api/app-types/validation.app';
import { ASSIGNMENT_API } from '../../../api/services/assignment.service';
import { VALIDATION_SERVICE } from '../../../api/services/validation.service';
import { PaginatedResponse } from '../../pagination/paginated-response';

export const activeAssignmentResolver: ResolveFn<Assignment | undefined> = async (
	route: ActivatedRouteSnapshot,
	_state: RouterStateSnapshot,
) => {
	const api = inject(ASSIGNMENT_API);
	const clientId = route.paramMap.get('id');
	const res = await api.getAll(Number.parseInt(clientId!));
	const assignment = res.find((a) => {
		const phaseRemark = a.phase?.remark?.toLowerCase() ?? '';
		const isFds = a.isServiceOfficeStatement === true || a.phaseId >= DB_PHASES.FP3 || phaseRemark.startsWith('fp');
		return !isFds && a.phaseId !== DB_PHASES.P4 && !a.isAdditionalAssignment;
	});

	return assignment;
};

export const assignmentResolver: ResolveFn<AssignmentWithRelations | undefined> = async (
	route: ActivatedRouteSnapshot,
	_state: RouterStateSnapshot,
) => {
	const api = inject(ASSIGNMENT_API);
	const clientId = route.paramMap.get('id');
	const assignmentId = route.paramMap.get('assignmentId');
	const res = await api.single(clientId!, assignmentId!);
	return res.data;
};

export const assignmentWithMetadataResolver: ResolveFn<
	ApiResponse<AssignmentWithRelations | undefined, Metadata>
> = async (route: ActivatedRouteSnapshot, _state: RouterStateSnapshot) => {
	const api = inject(ASSIGNMENT_API);
	const clientId = route.paramMap.get('id');
	const assignmentId = route.paramMap.get('assignmentId');
	const res = await api.single(clientId!, assignmentId!);
	return res;
};

export const typeOfCareIdByAssignmentResolver: ResolveFn<number | undefined> = async (
	route: ActivatedRouteSnapshot,
	_state: RouterStateSnapshot,
) => {
	const api = inject(ASSIGNMENT_API);
	const clientId = route.paramMap.get('id');
	const assignmentId = route.paramMap.get('assignmentId');
	const res = await api.single(clientId!, assignmentId!);
	const assignment = res.data;
	if (!assignment) return undefined;

	if (!assignment.isAdditionalAssignment) {
		return assignment.typeOfCareId ?? assignment.typeOfCare?.id;
	}

	// Additional assignment: tasks endpoint must use parent main assignment typeOfCareId.
	const allAssignments = await api.getAll(Number(clientId));
	const entities = allAssignments ?? [];

	const parentFromNested = entities.find(
		(a) => !a.isAdditionalAssignment && (a.additionalAssignments ?? []).some((x) => x.id === assignment.id),
	);
	if (parentFromNested?.typeOfCareId != null) return parentFromNested.typeOfCareId;

	const nonAdditional = entities.filter((a) => !a.isAdditionalAssignment && !a.isServiceOfficeStatement);
	if (nonAdditional.length === 1) return nonAdditional[0].typeOfCareId;

	const activeMain = nonAdditional.find((a) => a.clientId === assignment.clientId && a.phaseId !== DB_PHASES.P4);
	return activeMain?.typeOfCareId ?? nonAdditional[0]?.typeOfCareId;
};

export const assignmentChangeResolver: ResolveFn<{
	assignment?: Assignment;
	validationErrors: ValidationPhaseResponse;
}> = async (route: ActivatedRouteSnapshot, _state: RouterStateSnapshot) => {
	const api = inject(ASSIGNMENT_API);
	const validator = inject(VALIDATION_SERVICE);
	const clientId = route.paramMap.get('id');
	const assignmentId = route.paramMap.get('assignmentId');
	if (!clientId || !assignmentId) {
		return {
			assignment: undefined,
			validationErrors: {
				general: [
					{
						severity: 'error',
						message: 'Achtung: Validierung auf Server fehlgeschlagen. Bitte kontaktieren Sie den Support.',
						property: 'General',
					},
				],
			},
		};
	}
	const res = await api.single(clientId!, assignmentId!);
	const isServiceOfficeStatement = res.data?.isServiceOfficeStatement;
	const isAdditionalAssignment = res.data?.isAdditionalAssignment;
	const rawPhaseChangeId = res.data?.phaseChangeId;
	const targetPhaseId = isServiceOfficeStatement
		? rawPhaseChangeId === DB_PHASES.P4 || !rawPhaseChangeId
			? DB_PHASES.FP4
			: rawPhaseChangeId
		: (rawPhaseChangeId ?? DB_PHASES.P4);
	const validate = isServiceOfficeStatement
		? validator.validateServiceOfficeStatementPhaseTransition(assignmentId!, Number.parseInt(clientId!), targetPhaseId)
		: isAdditionalAssignment
			? validator.validateAdditionalAssignmentPhaseTransition(assignmentId!, Number.parseInt(clientId!), targetPhaseId)
			: validator.validatePhaseTransition(
					Number.parseInt(clientId!),
					assignmentId!,
					res.data!.phase.id!,
					targetPhaseId,
				);
	const validationResult = await validate.catch(() => {
		console.warn('Validation for phase transition failed');
		const data: ValidationPhaseResponse = {
			general: [
				{
					severity: 'error',
					message: 'Achtung: Validierung auf Server fehlgeschlagen. Bitte kontaktieren Sie den Support.',
					property: 'General',
				},
			],
		};
		return data;
	});
	return {
		assignment: res.data,
		validationErrors: validationResult,
	};
};

export const dateRangeResolverDueToDate: ResolveFn<AssingmentImpedimentReportMinMaxDueDate> = async (
	route: ActivatedRouteSnapshot,
	_state: RouterStateSnapshot,
) => {
	const api = inject(ASSIGNMENT_API);
	const assignmentId = Number(route.paramMap.get('assignmentId'));
	const response = await api.getValidDateRanges(assignmentId);
	return response;
};

export const assignmentsResolver: ResolveFn<Assignment[]> = async (
	route: ActivatedRouteSnapshot,
	_state: RouterStateSnapshot,
) => {
	const api = inject(ASSIGNMENT_API);
	const clientId = route.paramMap.get('id');
	const res = await api.getAll(Number.parseInt(clientId!));
	return res;
};

export const endOfCaresResolver: ResolveFn<PaginatedResponse<AssignmentEndOfCare>> = async (
	route: ActivatedRouteSnapshot,
	_state: RouterStateSnapshot,
) => {
	const api = inject(ASSIGNMENT_API);
	const assignmentId = route.paramMap.get('assignmentId');
	if (!assignmentId) throw new Error('No assignment ID provided');
	const env = inject(ENVIRONMENT);

	return await api
		.assignmentEndOfCares(assignmentId, env.pagination.offset, env.pagination.range)
		.catch((e: unknown) => {
			const err = e as Partial<HttpErrorResponse>;
			const status = err?.status;
			const detail: string | undefined = err?.error?.detail;

			// Specific case that Career is missing
			if (status === 404 && typeof detail === 'string' && detail.includes('Career with Id')) {
				return {
					totalEntities: 0,
					range: env.pagination.range,
					offset: env.pagination.offset,
					entities: [],
				} satisfies PaginatedResponse<AssignmentEndOfCare>;
			}

			// Throw other errors
			throw e;
		});
};
